import { Directive, Inject, Input, Optional } from '@angular/core';
import { FQAN_nadred_jedgrad } from '@app/components';
import {
  EarhivTreeNode,
  TreeBusinessItem
} from '@app/components/hierarhija/types';
import {
  AttributeStateService,
  BusinessItem,
  CoreConfig,
  CoreConfigService,
  DataSource,
  Item,
  ListView,
  ListViewProviderDirective,
  ViewProviderDirective,
  ViewResultListener
} from '@ntap/core';
import { TreeNode } from 'primeng/api';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  first,
  flatMap,
  map,
  mergeMap,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

@Directive({
  selector: '[appTreeViewProvider]',
  exportAs: 'appTreeViewProvider',
  providers: [
    {
      provide: ListViewProviderDirective,
      useExisting: TreeViewProviderDirective
    },
    {
      provide: ViewProviderDirective,
      useExisting: TreeViewProviderDirective
    }
  ]
})
export class TreeViewProviderDirective extends ListViewProviderDirective<ListView> {
  public defaultPageLimit = 100;

  private rootViewStateSubject = new ReplaySubject<any>(1);
  private rootViewNameSubject = new ReplaySubject<string>(1);
  private refreshTreeSubject = new BehaviorSubject<boolean>(true);
  private _treeViewData$: Observable<TreeNode<Item>[]>;
  roots$: Observable<any[]>;
  private _selectedNodes: Array<TreeNode & EarhivTreeNode>;
  public idToNodeMap: Record<string, EarhivTreeNode> = {};
  private extraRoots = new BehaviorSubject<any[]>([]);

  constructor(
    @Optional() resultListener: ViewResultListener,
    dataSource: DataSource,
    @Inject(CoreConfigService) protected override coreConfig: CoreConfig,
    attributeStateService: AttributeStateService
  ) {
    super(resultListener, dataSource, coreConfig, attributeStateService);
    this.roots$ = combineLatest([
      this.view$,
      this.rootViewNameSubject,
      this.rootViewStateSubject
    ]).pipe(
      mergeMap(([, rootViewName, rootViewState]) =>
        this.dataSource.getViewData(rootViewName, rootViewState)
      ),
      mergeMap((roots) =>
        this.extraRoots.pipe(map((extraRoots) => [...roots, ...extraRoots]))
      )
    );
    this._treeViewData$ = this.refreshTreeSubject.pipe(
      switchMap(() => this.mapRoots(this.roots$)),
      tap((node) => this.pushToNodeMap(node))
    );
  }

  private pushToNodeMap(nodes: EarhivTreeNode[]) {
    nodes.forEach((node) => (this.idToNodeMap[node.id] = node));
  }

  @Input() mapRoots: (mapRoots: any) => Observable<TreeNode[]>;

  @Input() mapNodes: (response: any, parentNode?: TreeNode) => EarhivTreeNode[];

  @Input()
  set rootViewState(value: any) {
    this.rootViewStateSubject.next(value);
  }

  get rootViewState() {
    return this.rootViewStateSubject;
  }

  @Input()
  set rootViewName(value: any) {
    this.rootViewNameSubject.next(value);
  }

  // ovaj observable koriste Tree implementacije da bi dohvatile podatke
  get treeViewData$() {
    return this._treeViewData$;
  }

  get selectedNodes() {
    return this._selectedNodes;
  }

  set selectedNodes(
    nodes: Array<TreeNode & EarhivTreeNode> | (TreeNode & EarhivTreeNode)
  ) {
    this._selectedNodes = nodes instanceof Array ? nodes : [nodes];
    this.selectedItems = this._selectedNodes.map((node) => node.data);
  }

  override get viewData$() {
    return of([]);
  }

  override updateItem(item: Item, itemIndex: any) {
    const currentNode = this.idToNodeMap[itemIndex];
    const mapped = this.mapNodes([item], currentNode.parent);
    // TODO prebaciti na immutable, tj. naci parent od currentNode i sve njegove children,
    // i onda zamijeniti samo taj child s novim, a pushati kopirani ...children
    // onda bi mogli koristiti OnPush na tree-ju
    const updatedNode = mapped[0];
    const currentlyExpanded = currentNode.expanded;
    updatedNode.expanded = false; // zatvaramo, mozda su se children nakon updejta promijenile
    const newData = Object.assign({}, currentNode.data, updatedNode.data);
    Object.assign(currentNode, updatedNode);
    currentNode.data = newData;
    this.nodeRefresh(currentNode);
    currentNode.expanded = currentlyExpanded;
  }

  nodeExpand(
    node: EarhivTreeNode,
    newState: any,
    limit: number
  ): Observable<any> {
    if (!newState) {
      throw new Error('Selected node with unknown id');
    }
    return this.dataSource.getViewData(this.viewName, newState).pipe(
      map((response) => {
        node.offset = limit;
        node.children = this.mapNodes(response, node);
        this.pushToNodeMap(node.children);
      })
    );
  }

  loadMore(
    node: EarhivTreeNode,
    newState: any,
    limit: number
  ): Observable<any> {
    if (!newState) {
      throw new Error('Selected node with unknown id');
    }

    return this.dataSource.getViewData(this.viewName, newState).pipe(
      map((response) => {
        node.offset = (node.offset ?? 0) + limit;
        const childNodes = this.mapNodes(response, node);
        const dataNodes = childNodes.filter((n) => n.type != 'url'); // dok ne izbrisemo loadmore node iz mapNodes
        node.children?.splice(node.children.length - 1, 0, ...dataNodes);
        if (response.length < limit) {
          node.children?.pop();
        }
        this.pushToNodeMap(dataNodes);
        return response;
      })
    );
  }

  loadMoreRoots(newState: any) {
    return this.rootViewNameSubject.pipe(
      withLatestFrom(this.rootViewStateSubject),
      first(),
      mergeMap(([viewName, rootViewState]) =>
        this.dataSource
          .getViewData(viewName, Object.assign({}, rootViewState, newState))
          .pipe(
            filter((response) => !!response && response.length > 0),
            tap((response) => {
              // re-emit with new roots
              this.extraRoots.next([
                ...this.extraRoots.getValue(),
                ...response
              ]);
            })
          )
      )
    );
  }

  nodeCollapse(node: EarhivTreeNode) {
    if (node.children) {
      // TODO vjerojatno ce trebati optimizrati da ne radimo kopiju svih nodeova,
      // ako ih bude moglo biti puno selektiranih
      const newSelectedNodes = [...(this._selectedNodes || [])];
      // FIXME ovo treba zvati rekurzivno, da bi odselektirali sve koje smo collapsali
      node.children.forEach((child: EarhivTreeNode) => {
        // TODO make this type safe
        const index = newSelectedNodes.findIndex(
          (node) => node.id === child.id
        );
        if (index >= 0) {
          newSelectedNodes.splice(index, 1);
        }
      });
      this.selectedNodes = newSelectedNodes;
    }
    node.children = [];
  }

  nodeRefresh(node: EarhivTreeNode) {
    const requestState = Object.assign({}, this.viewState, {
      'en~jedinica_gradiva~nadredena_jedinica_gradiva': node.id
    });
    this.nodeCollapse(node);
    this.nodeExpand(node, requestState, this.defaultPageLimit).subscribe();
  }

  override addItem(item: Item): void {
    this.addTreeNodeChild(item);
  }
  @Input() rowIndex: number;

  private addTreeNodeChild(item: any) {
    if (
      !this._selectedNodes ||
      this._selectedNodes.length <= 0 ||
      !item[FQAN_nadred_jedgrad]
    ) {
      // dodajemo zbirku, znaci treba refreshat cijelo stablo
      this.refreshTreeSubject.next(true);
      return;
    }

    const selectedParentNode = this._selectedNodes[0];

    const children = this.mapNodes([item], selectedParentNode);

    if (selectedParentNode.children) {
      selectedParentNode.children = [
        ...selectedParentNode.children,
        ...children
      ];
    } else {
      selectedParentNode.children = children;
    }
    //refreshamo samo selektirani node
    this.nodeRefresh(selectedParentNode);
  }

  override deleteItem(item: BusinessItem) {
    this.removeTreeNodeChild(item.data);
    this.selectedNodes = [];
    // ne zovemo super jer ne zelimo refresh cijele liste, ali budemo kad NtapListViewProvider bude podrzavao
    // partial refresh - onda cemo moci obrisati i eksplicitni refreshTreeSubject
    // super.deleteItem(item);
    // this.refreshTreeSubject.next(true);
  }

  private removeTreeNodeChild(item: TreeBusinessItem) {
    const nodeChildren = item.$parentNode.children;
    if (nodeChildren) {
      const nodeToDeleteIndex = nodeChildren.findIndex(
        (child) => child.data.id == item.id
      );
      if (nodeToDeleteIndex != -1) {
        nodeChildren.splice(nodeToDeleteIndex, 1);
      }
    }
  }

  override deleteItemAtIndex(item: BusinessItem, index: number) {
    if (item instanceof TreeBusinessItem) {
      this.removeTreeNodeChild(item.data);
    }
    return super.deleteItemAtIndex(item, index);
  }

  override deleteSelectedItems() {
    // FIXME trebalo bi u frameworku promijeniti da delete vraca array poslanih i array brisanih
    // sa servera. Na taj nacin se moze extendati ponasanje bez kopiranja ovog koda
    return this.selectedItems$.pipe(
      first(),
      flatMap((selectedItems) => {
        return this.dataSource
          .deleteViewData(
            this.viewName,
            this.transformItemsToBasicData(selectedItems)
          )
          .pipe(
            first(),
            tap(() => {
              selectedItems.forEach((item: any) =>
                this.removeTreeNodeChild(item)
              );
              this.selectedItems = [];
            })
          );
      })
    );
  }

  public removeSelectedNodes() {
    this._selectedNodes.forEach((node) => this.removeTreeNodeChild(node.data));
    this.selectedNodes = [];
  }

  nodeRefreshById(id: string) {
    const node = this.idToNodeMap[id];
    if (node) {
      this.nodeRefresh(node);
    }
  }

  refreshNodeParent(id: string) {
    const node = this.idToNodeMap[id];
    if (node) {
      if (node.parent) {
        this.nodeRefresh(node.parent);
      }
    }
  }

  closeNode(id: string | number) {
    this.toggleNode(id, false);
  }

  closeNodes() {
    const keys = Object.keys(this.idToNodeMap);
    keys.forEach((k) => (this.idToNodeMap[k].expanded = false));
  }

  expandNode(id: string | number) {
    this.toggleNode(id, true);
  }

  toggleNode(id: string | number, isExpanded: boolean) {
    const node = this.idToNodeMap[id];
    if (node) {
      node.expanded = isExpanded;
    }
  }
}
